/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, May 6th 2022, 9:54:46 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { getPurchaseFlowStrategyHook } from "saas-subscription/strategy";
import { SubscriptionPlan } from "saas-subscription/schemas";
import { useSaaSSubscriptionAPI } from "saas-subscription/services/SubscriptionAPI";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import { OSDialog, useToggleState } from "ui-widgets";
import { STATUS_CODES } from "lib/api/types";
import { SubscriptionPurchase } from "./SubscriptionPurchase";
import { SubscriptionPlanCard } from "./SubscriptionPlanCard";
import { SubscriptionServiceUnreachable } from "./SubscriptionServiceUnreachable";

interface SubscriptionPlansProps {
  toShowCodes?: SubscriptionPlan["metadata"]["product_code"][];
}

export const SubscriptionPlans = ({ toShowCodes }: SubscriptionPlansProps) => {
  const { useSubscriptionPlansList } = useSaaSSubscriptionAPI();
  const { subscriptionPlans, httpResponseStatusCode } = useSubscriptionPlansList();
  const plansToShow = Boolean(toShowCodes)
    ? subscriptionPlans.filter((plan) => toShowCodes?.includes(plan.metadata.product_code))
    : subscriptionPlans;
  const gridColumns = 60;
  const gridItemSize = Math.floor(gridColumns / plansToShow.length);
  const [selectedPlan, setSelectedPlan] = React.useState<SubscriptionPlan>();
  const [priceInterval, setPriceInterval] =
    React.useState<SubscriptionPlan["prices"][number]["recurring"]["interval"]>("month");

  const handlePriceIntervalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPriceInterval(event.target.value as SubscriptionPlan["prices"][0]["recurring"]["interval"]);
  };

  const {
    handleClose: handleClosePurchaseModal,
    handleOpen: handleOpenPurchaseModal,
    open: purchaseModalOpen,
  } = useToggleState();

  const handlePlanSelection = (subscriptionPlan) => {
    setSelectedPlan(subscriptionPlan);
    handleOpenPurchaseModal();
  };
  const usePurchaseFlowStrategy = getPurchaseFlowStrategyHook(selectedPlan);
  const purchaseFlowStrategy = usePurchaseFlowStrategy();

  return httpResponseStatusCode === STATUS_CODES.UnreachableServerErrorCode ? (
    <SubscriptionServiceUnreachable />
  ) : (
    <APIListProgressWrapper
      items={subscriptionPlans}
      showLoader={subscriptionPlans.length === 0}
      isAPIError={false}
      httpResponseStatusCode={0}
    >
      {selectedPlan && (
        <OSDialog open={purchaseModalOpen} onClose={handleClosePurchaseModal}>
          <SubscriptionPurchase
            strategy={purchaseFlowStrategy}
            subscriptionPlan={selectedPlan}
            priceInterval={priceInterval}
          />
        </OSDialog>
      )}

      <Stack spacing={4}>
        <Stack direction="row" justifyContent="center">
          <PricingInterval value={priceInterval} onChange={handlePriceIntervalChange} />
        </Stack>

        <Grid container spacing={2} className="os-subscription-plan-list" columns={gridColumns}>
          {plansToShow.map((subscriptionPlan) => (
            // Enterprise card is full width at sm breakpoint
            <Grid item key={`${subscriptionPlan.id}`} xs={gridColumns as any} md={gridItemSize as any}>
              <SubscriptionPlanCard
                priceInterval={priceInterval}
                subscriptionPlan={subscriptionPlan}
                handlePlanSelection={handlePlanSelection}
              />
            </Grid>
          ))}
        </Grid>
      </Stack>
    </APIListProgressWrapper>
  );
};

const PricingInterval = ({ value, onChange }) => {
  return (
    <RadioGroup
      row
      aria-labelledby="price-interval-selection"
      name="price-interval-buttons-group"
      className="price-interval-selection"
      onChange={onChange}
      value={value}
    >
      <div className="price-interval-selection-item">
        <FormControlLabel value="month" control={<Radio />} label="Monthly" />
      </div>
      <div className="price-interval-selection-item">
        <FormControlLabel value="year" control={<Radio />} label="Yearly (Save 20%)" />
      </div>
    </RadioGroup>
  );
};
